import * as React from 'react'

import { ArrowDownUp, Group, ListFilter, type LucideIcon } from 'lucide-react'

import { Badge } from '~/client/src/shared/components/shadcn-ui/badge'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'

export type FacetTriggerType = 'group' | 'sort' | 'filter'

const TYPE_ICONS: Record<FacetTriggerType, LucideIcon> = {
  group: Group,
  sort: ArrowDownUp,
  filter: ListFilter,
}

export interface IFacetTriggerProps extends Omit<
  React.ComponentProps<typeof Button>,
  'children'
> {
  label: string
  icon?: React.ReactNode
  badgeCount?: number
  iconOnly?: boolean
}

interface IInternalProps extends IFacetTriggerProps {
  facet: FacetTriggerType
}

const FacetTrigger = React.forwardRef<HTMLButtonElement, IInternalProps>(
  function FacetTrigger(
    {
      facet,
      label,
      icon,
      badgeCount,
      iconOnly,
      variant = 'outline',
      'aria-label': ariaLabel,
      ...rest
    },
    ref,
  ) {
    const TypeIcon = TYPE_ICONS[facet]
    const showBadge = typeof badgeCount === 'number' && badgeCount > 0

    return (
      <Button
        ref={ref}
        variant={variant}
        aria-label={ariaLabel ?? (iconOnly ? label : undefined)}
        {...rest}
      >
        {icon ?? <TypeIcon aria-hidden="true" />}
        {!iconOnly && (
          <span className="tw:max-w-[250px] tw:overflow-hidden tw:text-ellipsis tw:whitespace-nowrap">
            {label}
          </span>
        )}
        {showBadge && (
          <Badge className="tw:min-w-5 tw:px-1">{badgeCount}</Badge>
        )}
      </Button>
    )
  },
)

export const GroupButton = React.forwardRef<
  HTMLButtonElement,
  IFacetTriggerProps
>(function GroupButton(props, ref) {
  return <FacetTrigger ref={ref} facet="group" {...props} />
})

export const SortButton = React.forwardRef<
  HTMLButtonElement,
  IFacetTriggerProps
>(function SortButton(props, ref) {
  return <FacetTrigger ref={ref} facet="sort" {...props} />
})

export const FilterButton = React.forwardRef<
  HTMLButtonElement,
  IFacetTriggerProps
>(function FilterButton(props, ref) {
  return <FacetTrigger ref={ref} facet="filter" {...props} />
})
